import { useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useNavigate } from 'react-router'
import { ArrowDropDown, Check, CloseOutlined, SearchOutlined } from '@mui/icons-material'
import { Box, Button, debounce, IconButton, InputAdornment, InputBase, LinearProgress, Menu, MenuItem, MenuList, Paper, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { AppContext } from '../../providers/app-provider'
import formatApiData from '../../routes/search-results/format-api-data'
import { fetchSearchResults, navigateSearchResults, useSearchFilters } from '../../routes/search-results/helpers'
import { capitalizeFirstLetter } from '../../utils/helpers'
import useSharedStyles from '../../utils/shared.styles'

import ResultAccountItem from './result-items/account-item'
import ResultDeviceItem from './result-items/device-item'
import NoResultItem from './result-items/no-result-item'
import ResultOrderItem from './result-items/order-item'
import ResultPeopleItem from './result-items/people-item'
import RecentSearchItem from './result-items/recent-item'
import { addToRecent, getPlaceholderText, getRecents } from './helpers'
import useStyles from './index.styles'

const Search = ({
  closeOverlay,
  allowedTypes = ['accounts', 'devices', 'orders', 'people'],
  onSelect,
}: {
  closeOverlay?: () => void
  allowedTypes?: SearchTabsType[]
  onSelect?: (item: ISearchResultAccount | ISearchResultPerson | ISearchResultDevice | ISearchResultOrder) => void
}) => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const navigate = useNavigate()
  const { tree, roles, pageRefreshCounter } = useContext(AppContext)
  const auth = useAuth()
  const { q } = useSearchFilters()

  const [searchValue, setSearchValue] = useState(q)
  const [searchFilters] = useState<IFilterOptions>({
    folderTypes: [],
    roleNames: [],
    properties: [],
    statuses: [],
    hideDeactivated: false,
    excludeOptOut: false,
    hideNonProduction: false,
  })
  const [selectedSearchObject, setSelectedSearchObject] = useState<SearchObjectType>(
    allowedTypes.length === 1 ? (capitalizeFirstLetter(allowedTypes[0]) as SearchObjectType) : null
  )
  const [menuAnchor, setMenuAnchor] = useState<null | SVGSVGElement | HTMLElement>(null)

  const inputRef = useRef<HTMLInputElement>(undefined)

  const recentItems = useMemo(() => getRecents(), []) // REVIEW: is there any reason to fetch recents more than once?
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [data, setData] = useState<SearchResultsType | null>(null)
  const [hasMoreResults, setHasMoreResults] = useState<boolean>(false)
  const [resultsTypes, setResultsTypes] = useState<SearchTabsType[]>(['accounts', 'people', 'devices', 'orders'] as SearchTabsType[])
  const numberOfItems = data?.length ?? 0

  const menuOpen = Boolean(menuAnchor)

  const tab = selectedSearchObject?.toLowerCase() as SearchTabsType

  const theme = useTheme()

  // Change search value when the query changes
  useEffect(() => {
    setSearchValue(q)
  }, [q])

  useEffect(() => {
    if (closeOverlay) inputRef.current?.focus()
  }, [closeOverlay])

  const fetchSuggestions = async (newValue: string) => {
    if (!newValue || !tree || !auth.user?.access_token) return
    if (tab) {
      setError(false)
      setLoading(true)

      const res = await fetchSearchResults(newValue, tab, searchFilters, roles, auth.user.access_token, 10, 0)
      if (res.ok) {
        let tenants
        if (tab === 'accounts') tenants = (res.body as IGlobalSearchFolders).tenantFolders
        if (tab === 'people') tenants = (res.body as IGlobalSearchUsers).tenantUsers
        if (tab === 'devices') tenants = (res.body as IGlobalSearchNodes).tenantNodes
        if (tab === 'orders') tenants = (res.body as IGlobalSearchOrders).tenantOrders
        if (!tenants) return

        const allRecords: (ISearchResultPersonApi & { tenantId: number })[] = []
        let apiHasMore = false
        Object.entries(tenants).forEach(([tenantId, tenantData]) => {
          if (tenantData.hasMore) apiHasMore = true
          allRecords.push(
            ...tenantData.rows.map((item: IGlobalSearchFolders | IGlobalSearchUsers | IGlobalSearchNodes | IGlobalSearchOrders) => ({
              ...item,
              tenantId: Number(tenantId),
            }))
          )
        })

        // Limit the data to {maxResults} suggestions and set hasMoreResults if there are more than {maxResults} results
        const formattedApiData = formatApiData(tab, allRecords, tree)
        setData(formattedApiData)
        setHasMoreResults(apiHasMore)
      } else setError(true)
      setLoading(false)
    } else {
      setError(false)
      setLoading(true)

      const [accountsRes, peopleRes, devicesRes, ordersRes] = await Promise.all([
        fetchSearchResults(newValue, 'accounts', searchFilters, roles, auth.user.access_token, 5, 0),
        fetchSearchResults(newValue, 'people', searchFilters, roles, auth.user.access_token, 5, 0),
        fetchSearchResults(newValue, 'devices', searchFilters, roles, auth.user.access_token, 5, 0),
        fetchSearchResults(newValue, 'orders', searchFilters, roles, auth.user.access_token, 5, 0),
      ])
      setLoading(false)

      if (!accountsRes.ok || !peopleRes.ok || !devicesRes.ok || !ordersRes.ok) {
        setError(true)
        return
      }

      const peopleRecords: (ISearchResultPersonApi & { tenantId: number })[] = []
      let peopleHasMore = false
      Object.entries((peopleRes.body as IGlobalSearchUsers).tenantUsers).forEach(([tenantId, tenantData]) => {
        if (tenantData.hasMore) peopleHasMore = true
        peopleRecords.push(...tenantData.rows.map((person) => ({ ...person, tenantId: Number(tenantId) })))
      })
      const deviceRecords: (ISearchResultDeviceApi & { tenantId: number })[] = []
      let devicesHasMore = false
      Object.entries((devicesRes.body as IGlobalSearchNodes).tenantNodes).forEach(([tenantId, tenantData]) => {
        if (tenantData.hasMore) devicesHasMore = true
        deviceRecords.push(...tenantData.rows.map((device) => ({ ...device, tenantId: Number(tenantId) })))
      })
      const orderRecords: (ISearchResultOrderApi & { tenantId: number })[] = []
      let ordersHasMore = false
      Object.entries((ordersRes.body as IGlobalSearchOrders).tenantOrders).forEach(([tenantId, tenantData]) => {
        if (tenantData.hasMore) ordersHasMore = true
        orderRecords.push(...tenantData.rows.map((order) => ({ ...order, tenantId: Number(tenantId) })))
      })
      const accountRecords: (ISearchResultAccountApi & { tenantId: number })[] = []
      let accountsHasMore = false
      Object.entries((accountsRes.body as IGlobalSearchFolders).tenantFolders).forEach(([tenantId, tenantData]) => {
        if (tenantData.hasMore) accountsHasMore = true
        accountRecords.push(...tenantData.rows.map((account) => ({ ...account, tenantId: Number(tenantId) })))
      })

      // Sort all results by their score, then limit each category to 5 since global search returns up to 5 results per tenant rather than up to 5 results total
      const allResults: SearchResultsType = [
        ...formatApiData('people', peopleRecords.sort((a, b) => b.score - a.score).slice(0, 5), tree),
        ...formatApiData('devices', deviceRecords.sort((a, b) => b.score - a.score).slice(0, 5), tree),
        ...formatApiData('orders', orderRecords.sort((a, b) => b.score - a.score).slice(0, 5), tree),
        ...formatApiData('accounts', accountRecords.sort((a, b) => b.score - a.score).slice(0, 5), tree),
      ]

      const newResultsTypes: SearchTabsType[] = []
      if (accountRecords.length > 0) newResultsTypes.push('accounts')
      if (peopleRecords.length > 0) newResultsTypes.push('people')
      if (deviceRecords.length > 0) newResultsTypes.push('devices')
      if (orderRecords.length > 0) newResultsTypes.push('orders')
      setResultsTypes(newResultsTypes)

      const newData = allResults

      const allHasMore = accountsHasMore || peopleHasMore || ordersHasMore || devicesHasMore
      setData(newData)
      setHasMoreResults(allHasMore)
    }
  }

  const navigateTo = (currentSearchValue: string, item?: ISearchResultAccount | ISearchResultPerson | ISearchResultDevice | ISearchResultOrder) => {
    const id = item?.id ?? null
    const tenantId = item?.tenantId ?? null
    const entityType = item?.entityType ?? 'query'
    addToRecent({ id, entityType, searchQuery: currentSearchValue, item: item ?? null })
    if (entityType === 'query')
      navigateSearchResults(currentSearchValue, (selectedSearchObject?.toLowerCase() as SearchTabsType) ?? 'accounts', searchFilters, navigate)
    else navigate(`/tenant/${tenantId}/${entityType}/${id}`)

    // clear the focus from the input/menu items on navigation
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
      closeOverlay?.()
    }
  }

  const getSearchResults = debounce((value) => fetchSuggestions(value), 500)
  const getSearchResultsEffectEvent = useEffectEvent(getSearchResults)

  useEffect(() => {
    getSearchResultsEffectEvent(searchValue)
  }, [searchValue, tree, tab, searchFilters, roles, pageRefreshCounter])

  return (
    <>
      <Paper sx={classes.mainPaper}>
        <InputBase
          placeholder={getPlaceholderText(selectedSearchObject)}
          fullWidth={true}
          value={searchValue}
          inputRef={inputRef}
          onChange={(newValue) => setSearchValue(newValue.target.value)}
          onKeyDown={async (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            if (searchValue === '') return
            if (event.key === 'Enter') {
              const selectedItem = numberOfItems === 1 ? data?.[0] : undefined
              // redirect to the correct page based on the single item
              if (numberOfItems === 1 && selectedItem) {
                if (onSelect) onSelect(selectedItem)
                else navigateTo(searchValue, selectedItem)
              }
              // go to full search results if no suggestions or many suggestions
              else {
                if (onSelect) return
                navigateTo(searchValue)
              }
            } else if (event.key === ' ') {
              // Fetch suggestions after spacebar
              getSearchResults(searchValue)
            }
          }}
          startAdornment={
            allowedTypes.length > 1 ? (
              <InputAdornment position="start">
                <Button
                  onClick={(e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget)}
                  endIcon={<ArrowDropDown />}
                  sx={classes.searchTypeButton}
                  data-cy="searchType"
                >
                  {selectedSearchObject || 'Everything'}
                </Button>
                <Menu
                  anchorEl={menuAnchor}
                  id="search-options-menu"
                  open={menuOpen}
                  disableAutoFocusItem={true}
                  onClose={() => setMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <MenuItem onClick={() => setSelectedSearchObject(null)}>
                    <Button sx={{ color: !selectedSearchObject ? theme.palette.primary.main : theme.palette.text.secondary }}>
                      Everything{!selectedSearchObject && <Check sx={classes.searchTypeCheckIcon} />}
                    </Button>
                  </MenuItem>
                  {allowedTypes.includes('accounts') && (
                    <MenuItem onClick={() => setSelectedSearchObject('Accounts')}>
                      <Button sx={{ color: selectedSearchObject === 'Accounts' ? theme.palette.primary.main : theme.palette.text.secondary }}>
                        Accounts{selectedSearchObject === 'Accounts' && <Check sx={classes.searchTypeCheckIcon} />}
                      </Button>
                    </MenuItem>
                  )}
                  {allowedTypes.includes('people') && (
                    <MenuItem onClick={() => setSelectedSearchObject('People')}>
                      <Button sx={{ color: selectedSearchObject === 'People' ? theme.palette.primary.main : theme.palette.text.secondary }}>
                        People{selectedSearchObject === 'People' && <Check sx={classes.searchTypeCheckIcon} />}
                      </Button>
                    </MenuItem>
                  )}
                  {allowedTypes.includes('devices') && (
                    <MenuItem onClick={() => setSelectedSearchObject('Devices')}>
                      <Button sx={{ color: selectedSearchObject === 'Devices' ? theme.palette.primary.main : theme.palette.text.secondary }}>
                        Devices{selectedSearchObject === 'Devices' && <Check sx={classes.searchTypeCheckIcon} />}
                      </Button>
                    </MenuItem>
                  )}
                  {allowedTypes.includes('orders') && (
                    <MenuItem onClick={() => setSelectedSearchObject('Orders')}>
                      <Button sx={{ color: selectedSearchObject === 'Orders' ? theme.palette.primary.main : theme.palette.text.secondary }}>
                        Orders{selectedSearchObject === 'Orders' && <Check sx={classes.searchTypeCheckIcon} />}
                      </Button>
                    </MenuItem>
                  )}
                </Menu>
              </InputAdornment>
            ) : undefined
          }
          endAdornment={
            <>
              {searchValue && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchValue('')
                      setHasMoreResults(false)
                    }}
                    aria-label="Clear search term"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <CloseOutlined color="action" />
                  </IconButton>
                </InputAdornment>
              )}
              <InputAdornment position="end">
                <SearchOutlined color="primary" />
              </InputAdornment>
            </>
          }
          sx={classes.searchTextInputRoot}
        />
        <Paper id="secondaryPaper" sx={classes.secondaryPaper}>
          <Box sx={classes.resultsContainer}>
            {loading ? (
              <LinearProgress aria-label="Getting search suggestions..." />
            ) : (
              <Box
                sx={{
                  height: 4,
                }}
              />
            )}
            {error && (
              <Box
                sx={[
                  {
                    marginLeft: '34px',
                  },
                  classes.section,
                ]}
              >
                <Typography variant="body1" sx={sharedClasses.secondaryText}>
                  Having trouble loading suggestions.
                </Typography>
              </Box>
            )}
            {searchValue === '' && recentItems.length > 0 && (
              <MenuList sx={{ padding: 0 }}>
                <MenuItem tabIndex={-1} sx={classes.section}>
                  <Typography variant="body1" color="GrayText">
                    Recent searches
                  </Typography>
                </MenuItem>
                {recentItems.map((item, idx) => (
                  <RecentSearchItem
                    key={idx}
                    item={item}
                    onClick={() => {
                      if (onSelect) {
                        setSearchValue(item.searchQuery)
                        inputRef.current?.focus()
                      } else navigateTo(item.searchQuery)
                    }}
                  />
                ))}
              </MenuList>
            )}
            {searchValue !== '' && data && numberOfItems > 0 && (
              <MenuList>
                {resultsTypes.includes('accounts') && !tab && (
                  <MenuItem tabIndex={-1} sx={classes.resultType}>
                    Accounts
                  </MenuItem>
                )}
                {data
                  .filter((item) => item.entityType === 'accounts')
                  .map((item, idx) => (
                    <ResultAccountItem
                      key={idx}
                      name={(item as ISearchResultAccount)?.name}
                      accountNumber={(item as ISearchResultAccount).accountNumber}
                      address={(item as ISearchResultAccount).address}
                      folderType={(item as ISearchResultAccount).folderType}
                      parentName={(item as ISearchResultAccount).parentName}
                      isDeactivated={(item as ISearchResultAccount).isDeactivated}
                      searchValue={searchValue}
                      onClick={() => {
                        if (onSelect) onSelect(item)
                        else navigateTo(searchValue, item)
                      }}
                    />
                  ))}

                {resultsTypes.includes('devices') && !tab && (
                  <MenuItem tabIndex={-1} sx={classes.resultType}>
                    Devices
                  </MenuItem>
                )}
                {data
                  .filter((item) => item.entityType === 'devices')
                  .map((item, idx) => (
                    <ResultDeviceItem
                      key={idx}
                      vanity={(item as ISearchResultDevice).vanity}
                      nodeId={String((item as ISearchResultDevice).id)}
                      folderName={(item as ISearchResultDevice).folderName}
                      folderAddress={(item as ISearchResultDevice).address}
                      isDeactivated={(item as ISearchResultDevice).isDeactivated}
                      searchValue={searchValue}
                      onClick={() => {
                        if (onSelect) onSelect(item)
                        else navigateTo(searchValue, item)
                      }}
                    />
                  ))}

                {resultsTypes.includes('people') && !tab && (
                  <MenuItem tabIndex={-1} sx={classes.resultType}>
                    People
                  </MenuItem>
                )}
                {data
                  .filter((item) => item.entityType === 'people')
                  .map((item, idx) => (
                    <ResultPeopleItem
                      key={idx}
                      name={(item as ISearchResultPerson)?.name}
                      roleName={(item as ISearchResultPerson).roleName}
                      email={(item as ISearchResultPerson).email}
                      folderCount={(item as ISearchResultPerson).folderIds.length}
                      topLevelFolder={(item as ISearchResultPerson).parents} // REVIEW: Currently "parents" is the name of the topLevelFolder, but if product wants something different in search results we may need to change this
                      searchValue={searchValue}
                      onClick={() => {
                        if (onSelect) onSelect(item)
                        else navigateTo(searchValue, item)
                      }}
                    />
                  ))}

                {resultsTypes.includes('orders') && !tab && (
                  <MenuItem tabIndex={-1} sx={classes.resultType}>
                    Orders
                  </MenuItem>
                )}
                {data
                  .filter((item) => item.entityType === 'orders')
                  .map((item, idx) => (
                    <ResultOrderItem
                      key={idx}
                      shortOrderNumber={(item as ISearchResultOrder).shortOrderNumber}
                      orderType={(item as ISearchResultOrder).orderType}
                      status={(item as ISearchResultOrder).status}
                      folderName={(item as ISearchResultOrder).folderName}
                      folderAddress={(item as ISearchResultOrder).address}
                      accountNumber={(item as ISearchResultOrder).accountNumber}
                      contactName={(item as ISearchResultOrder).contactName}
                      contactEmail={(item as ISearchResultOrder).contactEmail}
                      searchValue={searchValue}
                      onClick={() => {
                        if (onSelect) onSelect(item)
                        else navigateTo(searchValue, item)
                      }}
                    />
                  ))}
              </MenuList>
            )}
            {searchValue !== '' && numberOfItems === 0 && !onSelect && (
              <MenuList>
                <NoResultItem searchQuery={searchValue} onClick={() => navigateTo(searchValue)} />
              </MenuList>
            )}
          </Box>
          {hasMoreResults && !onSelect && (
            <div style={{ ...classes.removeBottomSpace(theme), ...classes.hasMoreResultsContainer(theme) }}>
              <Button variant="text" color="primary" fullWidth={true} onClick={() => navigateTo(searchValue)}>
                View More
              </Button>
            </div>
          )}
        </Paper>
      </Paper>
    </>
  )
}

export default Search
